import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Artwork() {
  const { id } = useParams();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [work, setWork] = useState(null);
  const [missing, setMissing] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/artworks/${id}`)
      .then((r) => setWork(r.data))
      .catch(() => setMissing(true));
  }, [id]);

  const images = work ? (work.images?.length ? work.images : [work.image]) : [];

  const acquire = () => {
    sessionStorage.setItem("mas:enquire", JSON.stringify(work));
    navigate("/#contact");
  };

  return (
    <div data-testid="artwork-page" className="grain min-h-screen bg-ink text-parchment">
      <header className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-4 px-4 py-8 sm:px-8 lg:px-16">
        <Link
          to="/#boutique"
          data-testid="artwork-back-link"
          className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-sand transition-colors hover:text-gold"
        >
          <ArrowLeft size={13} /> {t.boutique.back_to_shop}
        </Link>
        <Link to="/" className="flex items-center gap-3">
          <img src="/images/logo.png" alt="Sceau de la Maison pour l'Art et la Science" className="h-8 w-8 object-contain" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-parchment/70">
            Maison pour l'Art et la Science
          </span>
        </Link>
      </header>

      {missing && (
        <div className="mx-auto max-w-[1400px] px-4 py-24 sm:px-8 lg:px-16">
          <p data-testid="artwork-not-found" className="font-serif text-2xl font-light italic text-sand">
            {t.boutique.not_found}
          </p>
        </div>
      )}

      {work && (
        <main className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-4 pb-24 pt-4 sm:px-8 lg:grid-cols-2 lg:gap-16 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <div className="gold-frame relative overflow-hidden border border-gold/20 bg-card2">
              <img
                data-testid="artwork-main-image"
                src={images[active]}
                alt={lang === "fr" ? work.title_fr : work.title_en}
                className="aspect-[4/5] w-full object-cover"
              />
            </div>
            {images.length > 1 && (
              <div className="mt-4 flex flex-wrap gap-3">
                {images.map((img, i) => (
                  <button
                    key={img}
                    data-testid="artwork-thumb"
                    onClick={() => setActive(i)}
                    className={`h-20 w-20 overflow-hidden border transition-colors duration-300 ${
                      i === active ? "border-gold" : "border-gold/20 hover:border-gold/60"
                    }`}
                  >
                    <img src={img} alt={`${lang === "fr" ? work.title_fr : work.title_en} — vue ${i + 1}`} loading="lazy" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="lg:pt-8"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold">
              {t.boutique.categories[work.category] || work.category}
            </p>
            <h1 data-testid="artwork-title" className="mt-4 font-serif text-4xl font-light leading-tight text-parchment sm:text-5xl">
              {lang === "fr" ? work.title_fr : work.title_en}
            </h1>
            <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-sand/80">
              {lang === "fr" ? work.medium_fr : work.medium_en || work.medium_fr}
            </p>
            <p className="mt-1 font-mono text-[10px] tracking-[0.15em] text-sand/60">
              {[work.year, work.dimensions].filter(Boolean).join(" · ")}
            </p>
            <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-sand sm:text-lg">
              {lang === "fr" ? work.description_fr : work.description_en}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-6 border-t border-gold/15 pt-8">
              <span data-testid="artwork-price" className="font-serif text-3xl italic text-gold">
                {work.price > 0 ? `${work.price} €` : t.boutique.on_request}
              </span>
              <button
                data-testid="artwork-acquire-button"
                onClick={acquire}
                className="group relative overflow-hidden rounded-full border border-gold/60 px-8 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-gold transition-colors duration-500 hover:text-ink"
              >
                <span className="absolute inset-0 -translate-x-full bg-gold transition-transform duration-500 ease-out group-hover:translate-x-0" />
                <span className="relative">{t.boutique.enquire}</span>
              </button>
            </div>
          </motion.div>
        </main>
      )}
    </div>
  );
}
